import React, {
    useEffect, useState, useCallback,
} from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Loader, useModal } from '@jutro/components';
import { withRouter } from 'react-router-dom';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { MLTagManagerService } from 'ml-portals-services-react';
import { MLLOBDisplayService } from 'ml-portals-services-js';

import MLGatewayCompRaterBridgeQuoteSummaryPage from './pages/MLGatewayCompRaterBridgeQuoteSummary/MLGatewayCompRaterBridgeQuoteSummaryPage';
import MLGatewayCompRaterWizardSideBar from './components/MLGatewayCompRaterWizardSideBar/MLGatewayCompRaterWizardSideBar';

import wizardConfig from './config/comp-wizard-config.json5';
import styles from './CompRaterWizard.module.scss';
import './CompRaterWizard.messages';

// Wizard Step Template for dynamic generation
const PA_WIZARD_STEP = {
    "id": "COMPAutoQuote",
    "title": { "id":"ml.quoteandbind.compRater.wizard.step.Auto Quote", "defaultMessage": "Auto Quote" },
    "mlStepName": "compRaterAutoQuotes",
    "status": "",
    "stepProps": {
      "sidebarConfig": {
        "stepHidden": false
      }
    }
}

const HO_WIZARD_STEP = {
    "id": "COMPHomeQuote",
      "title": { "id":"ml.quoteandbind.compRater.wizard.step.Home Quote", "defaultMessage": "Home Quote" },
      "mlStepName": "compRaterHomeQuotes",
      "status": "",
      "stepProps": {
        "sidebarConfig": {
          "stepHidden": false
        }
      }
}

const UMB_WIZARD_STEP = {
    "id": "COMPUmbrellaQuote",
    "title": { "id":"ml.quoteandbind.compRater.wizard.step.Umbrella Quote", "defaultMessage": "Umbrella Quote" },
    "mlStepName": "compRaterUMBQuotes",
    "status": "",
    "stepProps": {
      "sidebarConfig": {
        "stepHidden": false
      }
    }
}

function CompRaterWizard(props) {
    const modalApi = useModal();
    const { steps } = wizardConfig;
    const [initialBridgeQuoteData, setinitialBridgeQuoteData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [updatedSteps, setUpdatedSteps] = useState(steps);
    const {history } = props;
    const stateParams = _.get(history, 'location.state');

    const generateQuoteSteps = (quotes) => {
        if (quotes.length > 0) {
            const lobSteps = [];
    
            let hasPAQuote = false;
            let hasHOQuote = false;
            let hasUMBQuote = false;
    
            quotes.forEach((quote, idx) => {
                if(quote.policyLines[0].lineOfBusinessCode === MLLOBDisplayService.LOB.PERSONAL_AUTO) {
                    hasPAQuote = true;
                    const wizardStep = {
                        ...PA_WIZARD_STEP,
                        id: `${PA_WIZARD_STEP.id}-${idx}`,
                        status: quote.status !== "Quoted" ? '' : 'complete'
                    }
                    lobSteps.push(wizardStep);
                }
    
                if(quote.policyLines[0].lineOfBusinessCode === MLLOBDisplayService.LOB.HOMEOWNERS) {
                    hasHOQuote = true;
                    const wizardStep = {
                        ...HO_WIZARD_STEP,
                        id: `${HO_WIZARD_STEP.id}-${idx}`,
                        status: quote.status !== "Quoted" ? '' : 'complete'
                    }
                    lobSteps.push(wizardStep);
                }
    
                if(quote.policyLines[0].lineOfBusinessCode === MLLOBDisplayService.LOB.UMBRELLA) {
                    hasUMBQuote = true;
                    const wizardStep = {
                        ...UMB_WIZARD_STEP,
                        id: `${UMB_WIZARD_STEP.id}-${idx}`,
                        status: quote.status !== "Quoted" ? '' : 'complete'
                    }
                    lobSteps.push(wizardStep);
                }
            });

            const firstStep = steps[0];
            const lastStep = steps[1];

            let newSteps = [firstStep, ...lobSteps, lastStep];
    
            newSteps.forEach((newStep) => {
                // Hide Quote step in the sidebar if no such quote in the bridged quote bundle
                if(!hasPAQuote) {
                    if(newStep.id.indexOf('COMPAutoQuote') > -1) {
                        newStep.stepProps.sidebarConfig.stepHidden = true;
                    }
                } 
    
                if(!hasHOQuote) {
                    if(newStep.id === 'COMPHomeQuote') {
                        newStep.stepProps.sidebarConfig.stepHidden = true;
                    }
                }

                if(!hasUMBQuote) {
                    if(newStep.id.indexOf('COMPUmbrellaQuote') > -1) {
                        newStep.stepProps.sidebarConfig.stepHidden = true;
                    }
                }
            });

            return newSteps;
        }

        return steps;
    }

    useEffect(() => {
        const bridgedQuotes = stateParams?.bridgedQuotes || [];

        const bridgeQuoteData = {
            accountNumber: bridgedQuotes.length > 0 ? bridgedQuotes[0].accountNumber : null,
            accountHolderName: bridgedQuotes.length > 0 ? bridgedQuotes[0].displayName_ML : null,
            bridgedQuoteSummaries: [...bridgedQuotes],
        }

        setinitialBridgeQuoteData(bridgeQuoteData);
        setUpdatedSteps(generateQuoteSteps(bridgedQuotes));
        setIsLoading(false);
    }, []);

    // TODO Evaluate to Enable below functionality when implementing Analytics User Story
    // useEffect(() => {
    //     return () => {
    //         MLTagManagerService.setModel();
    //     }
    // }, []);


    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialBridgeQuoteData) {
        return null;
    }

    return (
        <div className={styles.digitalWizard}>
            <div className={styles.wizardNavigation}>
                <MLGatewayCompRaterWizardSideBar steps={updatedSteps} bridgedQuoteData={initialBridgeQuoteData} />    
            </div>
            <div className={styles.wizardMain}>
                <MLGatewayCompRaterBridgeQuoteSummaryPage 
                    bridgedQuoteData={
                    {
                        accountNumber:initialBridgeQuoteData?.accountNumber, 
                        isFromCompRater: true,
                        submissions: initialBridgeQuoteData?.bridgedQuoteSummaries || []
                    }
                } 
                />
            </div>
        </div>
    );
}

CompRaterWizard.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            submission: PropTypes.shape({})
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};

export default withRouter(CompRaterWizard);
